import React, { useEffect, useMemo, useState } from "react";
import { Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import CustomModal from "components/Common/CustomModal";
import { isValidCIN, isValidPhoneNumber } from "utils/validators";
import useAuthorizedPickUpPerson from "../hooks/useAuthorizedPickUpPerson";
import CommonSelectLite from "components/Common/CommonSelectLite";


type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  pickUpPersonData: any;
};

const AddEditAuthorizedPickUpPerson: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  pickUpPersonData
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const isEdit = !!pickUpPersonData?.id;
  const { relationshipOptions } = useAuthorizedPickUpPerson();
  useEffect(() => {
    if (!isOpen) return;

    if (!isEdit) {
      form.setFieldsValue({
        studentId: undefined,
        guardianId: undefined,
        fullName: "",
        cinNumber: "",
        phone: "",
        relationship: undefined,
        isDefaultGuardian: false,
      });
      return;
    }

    form.setFieldsValue({
      studentId: pickUpPersonData?.studentId,
      guardianId: pickUpPersonData?.guardianId,
      fullName: pickUpPersonData?.fullName || "",
      cinNumber: pickUpPersonData?.cinNumber || "",
      phone: pickUpPersonData?.phone || "",
      relationship: pickUpPersonData?.relationship,
      isDefaultGuardian: pickUpPersonData?.isDefaultGuardian || false,
    });
  }, [isOpen, isEdit, pickUpPersonData, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await onSave(values);
    } catch (err: any) {
      console.log(err);
    } finally {
      setSaving(false);
    }
  };


  return (
    <CustomModal
      title={isEdit ? t("authorizedPickUpPerson.edit") : t("authorizedPickUpPerson.add")}
      isOpen={isOpen}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      onSave={handleOk}
      saveText={t("save")}
      loading={saving}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          studentId: undefined,
          guardianId: undefined,
          fullName: "",
          cinNumber: "",
          phone: "",
          relationship: undefined,
          isDefaultGuardian: false,
        }}
      >

        <Form.Item
          label={t("authorizedPickUpPerson.form.fullName")}
          name="fullName"
          rules={[{ required: true, message: t("authorizedPickUpPerson.validation.fullNameRequired") }]}
        >
          <Input placeholder={t("authorizedPickUpPerson.placeholder.fullName")} />
        </Form.Item>

        <Form.Item
          label={t("authorizedPickUpPerson.form.cinNumber")}
          name="cinNumber"
          rules={[{ required: true, message: t("authorizedPickUpPerson.validation.cinNumberRequired") },
          {
            validator: (_, value) =>
              value && !isValidCIN(value)
                ? Promise.reject(t("staff.validation.cinNumber_invalid"))
                : Promise.resolve(),
          }
          ]}
        >
          <Input placeholder={t("authorizedPickUpPerson.placeholder.cinNumber")} />
        </Form.Item>

        <Form.Item
          label={t("authorizedPickUpPerson.form.phone")}
          name="phone"
          rules={[{ required: true, message: t("authorizedPickUpPerson.validation.phoneRequired") }, {
            validator: (_, value) =>
              value && !isValidPhoneNumber(value)
                ? Promise.reject(t("validation.phone_number_invalid"))
                : Promise.resolve(),
          },]}
        >
          <Input placeholder={t("authorizedPickUpPerson.placeholder.phone")} />
        </Form.Item>

        <Form.Item
          label={t("authorizedPickUpPerson.form.relationship")}
          name="relationship"
          rules={[{ required: true, message: t("authorizedPickUpPerson.validation.relationshipRequired") }]}
        >
          <CommonSelectLite
            options={relationshipOptions}
            placeholder={t("authorizedPickUpPerson.placeholder.selectRelationship")}
            optionValueKey="value"
            optionLabelKey="label"
            value={form.getFieldValue("relationship")}
            onChange={(value) => form.setFieldValue("relationship", value)}
          />
        </Form.Item>

      </Form>
    </CustomModal>
  );
};

export default AddEditAuthorizedPickUpPerson;